import { useQuery } from "@apollo/client";
import { useMemo, useState, useEffect } from "react";
import React from "react";
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from "@heroicons/react/24/outline";
import { ItemUsageStats } from "@/types/Profile";
import ItemUsageCard from "@/components/profile/ItemUsageCard";
import { GET_WARDROBE_ITEMS_QUERY } from "@/graphql/queries/wardrobe";
import { GET_OUTFITS_QUERY } from "@/graphql/queries";

interface WardrobeItem {
  id: string;
  item: string;
  imageUrl: string;
  category: {
    id: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface OutfitWithItems {
  id: string;
  createdAt: string;
  outfitItems: {
    id: string;
    category: {
      id: string;
      name: string;
    };
    item: string;
    colors: string[];
    style: string;
    imageUrl: string;
  }[];
}

function AnalyticsTab() {
  const [wardrobeItems, setWardrobeItems] = useState<WardrobeItem[]>([]);
  const [outfits, setOutfits] = useState<OutfitWithItems[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataFetched, setDataFetched] = useState(false);

  const {
    data: wardrobeData,
    loading: wardrobeLoading,
    error: wardrobeError,
  } = useQuery(GET_WARDROBE_ITEMS_QUERY, {
    fetchPolicy: "cache-first",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: false,
    skip: dataFetched, // Skip if we already have data
  });
  const {
    data: outfitsData,
    loading: outfitsLoading,
    error: outfitsError,
  } = useQuery(GET_OUTFITS_QUERY, {
    fetchPolicy: "cache-first",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: false,
    skip: dataFetched, // Skip if we already have data
  });

  // One-time data fetch and cache in local state
  useEffect(() => {
    if (!wardrobeLoading && !outfitsLoading && !dataFetched) {
      if (wardrobeError || outfitsError) {
        setError(
          wardrobeError?.message || outfitsError?.message || "Unknown error"
        );
        setLoading(false);
        return;
      }

      const items = wardrobeData?.getCurrentUser?.outfitItems || [];
      const outfitsArray = outfitsData?.getCurrentUser?.outfits || [];

      setWardrobeItems(items);
      setOutfits(outfitsArray);
      setLoading(false);
      setDataFetched(true);
    }
  }, [
    wardrobeData,
    outfitsData,
    wardrobeLoading,
    outfitsLoading,
    wardrobeError,
    outfitsError,
    dataFetched,
  ]);

  // Memoize usage statistics calculation to prevent infinite re-renders
  const usageStats = useMemo((): ItemUsageStats[] => {
    if (!wardrobeItems.length || !outfits.length) {
      return wardrobeItems.map((item) => ({
        id: item.id,
        item: item.item,
        imageUrl:
          item.imageUrl ||
          "https://images.unsplash.com/photo-1445205170230-053b83016050?w=300",
        category: item.category?.name || "Uncategorized",
        usageCount: 0,
        lastUsed: item.createdAt,
      }));
    }

    return wardrobeItems.map((item) => {
      // Count how many outfits this item appears in
      const outfitsWithItem = outfits.filter((outfit) =>
        outfit.outfitItems?.some((outfitItem) => outfitItem.id === item.id)
      );

      // Find the most recent outfit this item was used in
      const mostRecentOutfit = outfitsWithItem.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )[0];

      return {
        id: item.id,
        item: item.item,
        imageUrl:
          item.imageUrl ||
          "https://images.unsplash.com/photo-1445205170230-053b83016050?w=300",
        category: item.category?.name || "Uncategorized",
        usageCount: outfitsWithItem.length,
        lastUsed: mostRecentOutfit?.createdAt || item.createdAt,
      };
    });
  }, [wardrobeItems, outfits]);

  // Memoize sorted arrays to prevent unnecessary re-calculations
  const mostUsedItems = useMemo(() => {
    return usageStats.sort((a, b) => b.usageCount - a.usageCount).slice(0, 4);
  }, [usageStats]);

  const leastUsedItems = useMemo(() => {
    return usageStats.sort((a, b) => a.usageCount - b.usageCount).slice(0, 4);
  }, [usageStats]);

  if (error) {
    return (
      <div className="space-y-12">
        <div className="text-center">
          <h2 className="text-2xl font-extralight tracking-[0.3em] uppercase text-gray-900 dark:text-white">
            WARDROBE ANALYTICS
          </h2>
          <p className="mt-2 text-sm font-light tracking-wider text-red-600 dark:text-red-400">
            Error loading data: {error}
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-12">
        <div className="text-center">
          <h2 className="text-2xl font-extralight tracking-[0.3em] uppercase text-gray-900 dark:text-white">
            WARDROBE ANALYTICS
          </h2>
          <p className="mt-2 text-sm font-light tracking-wider text-gray-600 dark:text-gray-400">
            Analyzing your style patterns...
          </p>
        </div>
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
        </div>
      </div>
    );
  }

  if (wardrobeItems.length === 0) {
    return (
      <div className="space-y-12">
        <div className="text-center">
          <h2 className="text-2xl font-extralight tracking-[0.3em] uppercase text-gray-900 dark:text-white">
            WARDROBE ANALYTICS
          </h2>
          <p className="mt-2 text-sm font-light tracking-wider text-gray-600 dark:text-gray-400">
            No wardrobe items found
          </p>
        </div>
        <div className="text-center py-16">
          <ArrowTrendingUpIcon className="mx-auto h-16 w-16 text-gray-300 dark:text-gray-600" />
          <h3 className="mt-4 text-lg font-light tracking-wider text-gray-500 dark:text-gray-400">
            BUILD YOUR WARDROBE
          </h3>
          <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
            Add items to see usage analytics
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      <div className="text-center">
        <h2 className="text-2xl font-extralight tracking-[0.3em] uppercase text-gray-900 dark:text-white">
          WARDROBE ANALYTICS
        </h2>
        <p className="mt-2 text-sm font-light tracking-wider text-gray-600 dark:text-gray-400">
          Insights into your style patterns
        </p>
      </div>

      {/* Most Used Items */}
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <ArrowTrendingUpIcon className="h-6 w-6 text-green-500" />
          <h3 className="text-lg font-light tracking-wider uppercase text-gray-900 dark:text-white">
            MOST UTILIZED PIECES
          </h3>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {mostUsedItems.map((item) => (
            <ItemUsageCard key={item.id} item={item} type="most" />
          ))}
        </div>
      </div>

      {/* Least Used Items */}
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <ArrowTrendingDownIcon className="h-6 w-6 text-amber-500" />
          <h3 className="text-lg font-light tracking-wider uppercase text-gray-900 dark:text-white">
            UNDERUTILIZED PIECES
          </h3>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {leastUsedItems.map((item) => (
            <ItemUsageCard key={item.id} item={item} type="least" />
          ))}
        </div>
      </div>
    </div>
  );
}

// Wrap with React.memo to prevent unnecessary re-renders
export default React.memo(AnalyticsTab);
